/**
 * Shared customer journey status for field A/B/C steppers.
 * Stages align with docs/business-flows/flow-00-master-paygo-lifecycle.mmd.
 */

import { withEntityQuery } from "../../shared/navigation/entityPrefill";

export type JourneyStageId =
  | "lead"
  | "kyc"
  | "contract"
  | "payment"
  | "install"
  | "token";

export type JourneyStageStatus = "done" | "current" | "blocked" | "todo";

export const JOURNEY_STAGE_LABELS: Record<JourneyStageId, string> = {
  lead: "Lead",
  kyc: "KYC",
  contract: "Contract",
  payment: "Payment",
  install: "Install",
  token: "Token",
};

export const JOURNEY_STAGE_ORDER: JourneyStageId[] = [
  "lead",
  "kyc",
  "contract",
  "payment",
  "install",
  "token",
];

/** Facts known about a lead/customer — all optional; resolver fills gaps. */
export interface JourneyFacts {
  leadLocalId?: string;
  leadServerId?: number;
  customerId?: number;
  installationId?: number;
  leadCaptured?: boolean;
  kycSubmitted?: boolean;
  kycApproved?: boolean;
  hasActiveContract?: boolean;
  /** Deposit / first payment received (amount_paid > 0 or completed payment). */
  hasDepositOrPayment?: boolean;
  /** Device linked to customer (shown for install readiness; does not block payment). */
  hasDeviceAssigned?: boolean;
  installSubmittedOrApproved?: boolean;
  firstTokenIssued?: boolean;
}

export interface JourneyStageView {
  id: JourneyStageId;
  label: string;
  status: JourneyStageStatus;
  href: string;
  blocker?: string;
}

export interface JourneyResolved {
  stages: JourneyStageView[];
  /** First incomplete stage (current or blocked). */
  next: JourneyStageView | null;
  blockerSentence: string | null;
}

function hrefFor(id: JourneyStageId, facts: JourneyFacts): string {
  switch (id) {
    case "lead":
      if (facts.leadServerId != null) return `/field/leads/${facts.leadServerId}/duplicates`;
      if (facts.leadLocalId) return `/field/leads/${facts.leadLocalId}/duplicates`;
      return "/field/leads/new";
    case "kyc":
      if (facts.leadServerId != null) return `/field/leads/${facts.leadServerId}/kyc`;
      if (facts.leadLocalId) return `/field/leads/${facts.leadLocalId}/kyc`;
      return "/field/leads";
    case "contract":
      return facts.customerId != null
        ? `/customers/${facts.customerId}?contract=activate`
        : "/customers";
    case "payment":
      return facts.customerId != null
        ? withEntityQuery("/field/payments/new", { customer_id: facts.customerId })
        : "/field/payments/new";
    case "install":
      if (facts.installationId != null) {
        return `/field/installations/${facts.installationId}/scan`;
      }
      if (facts.customerId != null) {
        return withEntityQuery("/installations/new", { customer_id: facts.customerId });
      }
      return "/field/installations";
    case "token":
      return facts.installationId != null
        ? `/key-approvals/${facts.installationId}`
        : "/key-approvals";
    default:
      return "/field/leads";
  }
}

function blockerFor(id: JourneyStageId, facts: JourneyFacts): string | undefined {
  switch (id) {
    case "contract":
      if (!facts.kycSubmitted && !facts.kycApproved && !facts.customerId) {
        return "Complete KYC before creating a contract";
      }
      if (!facts.hasActiveContract) {
        return "No active contract — create a contract to record payment";
      }
      return undefined;
    case "payment":
      if (!facts.hasActiveContract) {
        return "Blocked: no active contract — create contract to record payment";
      }
      return undefined;
    case "install":
      if (!facts.hasDepositOrPayment) {
        return "Need deposit / first payment before installation GO";
      }
      if (!facts.hasDeviceAssigned) {
        return "No device assigned — assign or scan serial at install";
      }
      return undefined;
    case "token":
      if (!facts.installSubmittedOrApproved) {
        return "Submit installation for key approval first";
      }
      return undefined;
    default:
      return undefined;
  }
}

/**
 * Resolve done/current/blocked/todo for each lifecycle stage.
 * Payment is blocked without a contract. Device does not block payment.
 */
export function resolveJourney(facts: JourneyFacts): JourneyResolved {
  const leadDone = Boolean(facts.leadCaptured);
  const kycDone = Boolean(facts.kycSubmitted || facts.kycApproved);
  const contractDone = Boolean(facts.hasActiveContract);
  const paymentDone = Boolean(facts.hasDepositOrPayment);
  const installDone = Boolean(facts.installSubmittedOrApproved);
  const tokenDone = Boolean(facts.firstTokenIssued);

  const doneFlags: Record<JourneyStageId, boolean> = {
    lead: leadDone,
    kyc: kycDone,
    contract: contractDone,
    payment: paymentDone,
    install: installDone,
    token: tokenDone,
  };

  // First incomplete becomes current unless a hard dependency blocks it.
  let foundCurrent = false;
  const stages: JourneyStageView[] = JOURNEY_STAGE_ORDER.map((id) => {
    if (doneFlags[id]) {
      return {
        id,
        label: JOURNEY_STAGE_LABELS[id],
        status: "done" as const,
        href: hrefFor(id, facts),
      };
    }

    const blocker = blockerFor(id, facts);
    const hardBlocked =
      (id === "payment" && !contractDone) ||
      (id === "contract" && !kycDone && !facts.customerId) ||
      (id === "install" && (!paymentDone || !facts.hasDeviceAssigned)) ||
      (id === "token" && !installDone);

    if (hardBlocked && blocker) {
      if (!foundCurrent) {
        foundCurrent = true;
        return {
          id,
          label: JOURNEY_STAGE_LABELS[id],
          status: "blocked" as const,
          href: hrefFor(id === "payment" ? "contract" : id, facts),
          blocker,
        };
      }
      return {
        id,
        label: JOURNEY_STAGE_LABELS[id],
        status: "todo" as const,
        href: hrefFor(id, facts),
        blocker,
      };
    }

    if (!foundCurrent) {
      foundCurrent = true;
      return {
        id,
        label: JOURNEY_STAGE_LABELS[id],
        status: "current" as const,
        href: hrefFor(id, facts),
        blocker,
      };
    }

    return {
      id,
      label: JOURNEY_STAGE_LABELS[id],
      status: "todo" as const,
      href: hrefFor(id, facts),
      blocker,
    };
  });

  const next =
    stages.find((s) => s.status === "current" || s.status === "blocked") ?? null;
  const blockerSentence =
    next?.blocker ??
    stages.find((s) => s.blocker && (s.status === "blocked" || s.status === "current"))
      ?.blocker ??
    null;

  return { stages, next, blockerSentence };
}

/** Map local lead queue row → journey facts (pre-customer). */
export function factsFromLocalLead(lead: {
  localId: string;
  serverId?: number;
  status: string;
  convertedCustomerId?: number;
}): JourneyFacts {
  const captured = lead.status !== "draft" || Boolean(lead.serverId);
  const kycSubmitted = lead.status === "kyc-submitted";
  const closed = lead.status === "closed-merge" || lead.status === "closed-reject";
  return {
    leadLocalId: lead.localId,
    leadServerId: lead.serverId,
    customerId: lead.convertedCustomerId,
    leadCaptured: captured || closed || kycSubmitted || Boolean(lead.serverId),
    kycSubmitted,
    kycApproved: closed && lead.status === "closed-merge",
  };
}

/** Map customer search / detail row → journey facts. */
export function factsFromCustomer(customer: {
  id: number;
  active_contract?: { id: number; amount_paid?: number } | null;
  devices?: { id: number }[] | null;
  amount_paid?: number;
}): JourneyFacts {
  const paid =
    (customer.active_contract?.amount_paid ?? customer.amount_paid ?? 0) > 0;
  return {
    customerId: customer.id,
    leadCaptured: true,
    kycSubmitted: true,
    kycApproved: true,
    hasActiveContract: Boolean(customer.active_contract),
    hasDepositOrPayment: paid,
    hasDeviceAssigned: Boolean(customer.devices && customer.devices.length > 0),
  };
}

export function nextIncompleteHref(resolved: JourneyResolved): string | null {
  return resolved.next?.href ?? null;
}
